import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faUser,
  faBell,
  faSignOutAlt,
  faSearch, // Added for potential future search bar
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const Home = () => {
  const navigate = useNavigate();
  const [properties, setProperties] = useState([]);
  const [showAddPropertyForm, setShowAddPropertyForm] = useState(false);
  const [notificationsCount, setNotificationsCount] = useState(0);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    location: "",
    photo: "",
    county: "",
    owner_id: "", // Will be replaced with userId from local storage
  });
  const [photoUploaded, setPhotoUploaded] = useState(false); // State to track photo upload completion

  useEffect(() => {
    const userDataString = localStorage.getItem("user");
    const userData = JSON.parse(userDataString);
    const userId = userData.unique;
    const token = localStorage.getItem("token");

    if (token && userId) {
      // Fetch properties
      axios
        .get(
          `${StandardURL}/iNyumba/admin/properties/landlordproperties/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setProperties(response.data.response);
        })
        .catch((error) => {
          console.error("Error fetching properties:", error);
          toast.error("Failed to load properties. Please try again.");
        });

      // Fetch notifications count
      axios
        .get(
          `${StandardURL}/iNyumba/inappnotifications/viewallnotifications`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setNotificationsCount(response.data.response.length);
        })
        .catch((error) => {
          console.error("Error fetching notifications:", error);
        });
    }
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddPropertyClick = () => {
    setShowAddPropertyForm(true);
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formDataForUpload = new FormData();
    formDataForUpload.append("image", file);
    const token = localStorage.getItem("token");

    toast.info("Uploading photo...", { autoClose: false, toastId: "uploading" });

    try {
      const response = await axios.post(
        `${StandardURL}/iNyumba/images/images/upload`,
        formDataForUpload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setFormData((prevState) => ({
        ...prevState,
        photo: response.data.response.coverUri,
      }));
      setPhotoUploaded(true);
      toast.update("uploading", {
        render: "Photo uploaded successfully!",
        type: "success",
        autoClose: 3000,
      });
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.update("uploading", {
        render: "Error uploading photo.",
        type: "error",
        autoClose: 3000,
      });
    }
  };

  const handleSubmitProperty = async (e) => {
    e.preventDefault();

    if (!photoUploaded || !formData.photo) {
      toast.error("Please upload a property photo before submitting.");
      return;
    }

    try {
      const userDataString = localStorage.getItem("user");
      const userData = JSON.parse(userDataString);
      const userId = userData.unique;
      const token = localStorage.getItem("token");

      const response = await axios.post(
        `${StandardURL}/iNyumba/admin/properties/post`,
        {
          ...formData,
          owner_id: userId,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setProperties([...properties, response.data.response]);
      setFormData({
        title: "",
        description: "",
        location: "",
        photo: "",
        county: "",
        owner_id: "",
      });
      setShowAddPropertyForm(false);
      setPhotoUploaded(false); // Reset for next upload
      toast.success("Property added successfully!");
    } catch (error) {
      toast.error(error.response?.data?.response || "Failed to add property.");
      console.error("Error adding property:", error);
    }
  };

  const handleProfileClick = () => {
    navigate("/Landlord/profile");
  };

  const handleLogoutClick = () => {
    navigate("/logout");
  };

  const handlePropertyClick = (property_id) => {
    localStorage.setItem("selectedPropertyId", property_id);
    navigate(`/landlord/propertypage`);
  };

  const handleNotificationsClick = () => {
    navigate("/landlord/notifications");
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-4 sm:p-6 lg:p-8">
          {/* Header Section */}
          <header className="flex flex-col sm:flex-row justify-between items-center mb-8 p-4 bg-white shadow-md rounded-lg">
            <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-800 mb-4 sm:mb-0">
              My Properties
            </h1>
            <div className="flex items-center space-x-4">
              {/* Search Bar (Optional, but good for UX) */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search properties..."
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <FontAwesomeIcon
                  icon={faSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                />
              </div>

              {/* Notifications Icon */}
              <div
                className="relative cursor-pointer p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                onClick={handleNotificationsClick}
                title="Notifications"
              >
                <FontAwesomeIcon icon={faBell} className="text-xl text-gray-700" />
                {notificationsCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center border-2 border-white">
                    {notificationsCount}
                  </span>
                )}
              </div>
              {/* User Profile Icon */}
              <div
                className="cursor-pointer p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                onClick={handleProfileClick}
                title="Profile"
              >
                <FontAwesomeIcon icon={faUser} className="text-xl text-gray-700" />
              </div>
              {/* Logout Icon */}
              <div
                className="cursor-pointer p-2 rounded-full bg-red-100 hover:bg-red-200 transition-colors duration-200"
                onClick={handleLogoutClick}
                title="Logout"
              >
                <FontAwesomeIcon icon={faSignOutAlt} className="text-xl text-red-600" />
              </div>
            </div>
          </header>

          {/* Properties Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {properties.map((property) => (
              <div
                key={property.id}
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-102 transition-all duration-300 cursor-pointer overflow-hidden group"
                onClick={() => handlePropertyClick(property.property_id)}
              >
                {/* Property Image */}
                <div className="relative w-full h-48 sm:h-56 overflow-hidden">
                  <img
                    src={property.photo}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                  />
                  {/* Optional: Overlay on hover */}
                  <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="text-white text-lg font-semibold">View Details</p>
                  </div>
                </div>

                {/* Property Content */}
                <div className="p-5">
                  <h2 className="text-xl font-bold text-gray-900 mb-2 truncate">
                    {property.title}
                  </h2>
                  <p className="text-sm text-gray-600 mb-1">
                    <span className="font-semibold">Location:</span>{" "}
                    {property.location}
                  </p>
                  <p className="text-sm text-gray-600 mb-3">
                    <span className="font-semibold">County:</span> {property.county}
                  </p>
                  <p className="text-sm text-gray-700 leading-relaxed line-clamp-3">
                    {property.description || "No description provided."}
                  </p>
                </div>
              </div>
            ))}

            {/* Add Property Button / Form */}
            {showAddPropertyForm ? (
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-2xl font-bold text-gray-800 mb-4">Add New Property</h3>
                <form onSubmit={handleSubmitProperty} className="space-y-4">
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="Property Title"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Property Description"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 min-h-[90px]"
                    rows="3"
                    required
                  ></textarea>
                  <input
                    type="text"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    placeholder="Location (e.g., Street, Estate)"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <input
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleInputChange}
                    placeholder="County"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                  <div>
                    <label htmlFor="photo-upload" className="block text-sm font-medium text-gray-700 mb-2">Property Photo (JPG/PNG)</label>
                    <input
                      id="photo-upload"
                      type="file"
                      accept="image/*"
                      onChange={handleUploadPhoto}
                      className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 transition-colors duration-200"
                      required
                    />
                    {formData.photo && photoUploaded && (
                      <p className="text-green-600 text-sm mt-2">Photo ready for submission!</p>
                    )}
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={!photoUploaded}
                  >
                    Add Property
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddPropertyForm(false)}
                    className="w-full bg-gray-300 text-gray-800 px-6 py-3 rounded-lg font-semibold hover:bg-gray-400 transition-colors duration-300 mt-2"
                  >
                    Cancel
                  </button>
                </form>
              </div>
            ) : (
              <div
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transform hover:scale-102 transition-all duration-300 cursor-pointer flex flex-col items-center justify-center p-6 text-center border-2 border-dashed border-gray-300 hover:border-blue-500"
                onClick={handleAddPropertyClick}
              >
                <FontAwesomeIcon icon={faPlus} className="text-5xl text-blue-500 mb-4" />
                <p className="text-lg font-semibold text-gray-700">Add New Property</p>
                <p className="text-sm text-gray-500 mt-1">Click to add a new property listing.</p>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default Home;